/* kubota-featured-stories — parallax controller
   GSAP + ScrollTrigger are loaded globally in theme.liquid; do NOT re-import. */

class KubotaFeaturedStories extends HTMLElement {
  connectedCallback() {
    this._triggers = [];
    this._timelines = [];
    this._initialized = false;
    this._resizeRaf = null;

    this._resizeHandler = () => {
      if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = requestAnimationFrame(() => this._onResize());
    };
    window.addEventListener('resize', this._resizeHandler);

    this._tryInit();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._resizeHandler);
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
    this._teardown();
  }

  _shouldEnable() {
    if (this.dataset.parallaxEnabled === 'false') return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    // Desktop layout active ≥992px. 992-1199 fluidly scales cards down via CSS
    // clamp(); ≥1200 cards lock at design size 424×511 + 588×792.
    // Below 992 the section flips to mobile stack and parallax is disabled.
    if (window.innerWidth < 992) return false;
    return true;
  }

  _tryInit() {
    if (!this._shouldEnable()) return;

    if (window.gsap && window.ScrollTrigger) {
      this._init();
    } else {
      window.addEventListener(
        'load',
        () => {
          if (window.gsap && window.ScrollTrigger && !this._initialized && this._shouldEnable()) {
            this._init();
          }
        },
        { once: true }
      );
    }
  }

  _onResize() {
    const enabled = this._shouldEnable();
    if (enabled && !this._initialized) {
      this._tryInit();
    } else if (!enabled && this._initialized) {
      this._teardown();
      // Reset transforms left by GSAP — return cards/images to natural position.
      const cards = this.querySelectorAll('[data-parallax-card]');
      cards.forEach((card) => {
        card.style.transform = '';
      });
      const images = this.querySelectorAll('[data-parallax-image] img');
      images.forEach((img) => {
        img.style.transform = '';
      });
    }
  }

  _init() {
    this._initialized = true;
    const cards = this.querySelectorAll('[data-parallax-card]');
    if (cards.length === 0) return;

    const { gsap } = window;

    cards.forEach((card) => {
      const rate = parseFloat(card.dataset.parallaxRate) || -50;

      const cardTween = gsap.fromTo(
        card,
        { y: 0 },
        {
          y: rate,
          ease: 'none',
          scrollTrigger: {
            trigger: this,
            start: 'top bottom',
            end: () => `+=${window.innerHeight + this.offsetHeight}`,
            scrub: 1,
            invalidateOnRefresh: true,
          },
        }
      );

      this._timelines.push(cardTween);
      if (cardTween.scrollTrigger) this._triggers.push(cardTween.scrollTrigger);

      // Nested image parallax. GSAP owns the full transform stack (scale + yPercent)
      // to avoid composition conflicts with CSS transform. Image drifts -14% → +14%
      // within its frame; the 1.32 scale keeps edges covered through the drift
      // (16% headroom on each side > 14% drift).
      const image = card.querySelector('[data-parallax-image] img');
      if (image) {
        gsap.set(image, { scale: 1.32 });

        const imageTween = gsap.fromTo(
          image,
          { yPercent: -14 },
          {
            yPercent: 14,
            ease: 'none',
            scrollTrigger: {
              trigger: this,
              start: 'top bottom',
              end: () => `+=${window.innerHeight + this.offsetHeight}`,
              scrub: 1,
              invalidateOnRefresh: true,
            },
          }
        );

        this._timelines.push(imageTween);
        if (imageTween.scrollTrigger) this._triggers.push(imageTween.scrollTrigger);
      }
    });

    this.setAttribute('data-parallax-ready', '');
  }

  _teardown() {
    this.removeAttribute('data-parallax-ready');
    this._triggers.forEach((t) => t.kill());
    this._timelines.forEach((tl) => tl.kill());
    this._triggers = [];
    this._timelines = [];
    this._initialized = false;
  }
}

if (!customElements.get('kubota-featured-stories')) {
  customElements.define('kubota-featured-stories', KubotaFeaturedStories);
}
